import {
  PrivateImg,
  type PrivateImgProps,
} from "@winamp/media-upload/BytescaleContext/PrivateImg.js";
import type { VariantProps } from "class-variance-authority";
import { classNames } from "../../utils/classNames";
import { hashStringToNumber } from "../../utils/hash";
import { avatarVariants } from "./variants";

export type AvatarProps = {
  src?: string | null;
  initials?: string;
  alt?: string;
  className?: string;
  isPubliclyVisible?: boolean;
  imageTransformation?: PrivateImgProps["options"];
} & VariantProps<typeof avatarVariants>;

const colors = [
  "brand",
  "zinc",
  "red",
  "orange",
  "amber",
  "yellow",
  "lime",
  "green",
  "emerald",
  "teal",
  "cyan",
  "sky",
  "blue",
  "indigo",
  "violet",
  "purple",
  "fuchsia",
  "pink",
  "rose",
] as const;

/**
 * Use this function to calculate an avatar color based on the entity Unique ID.
 *
 * @param value An entity Unique ID
 * @returns An avatar color based on the entity Unique ID
 */
export function calculateAvatarColor(value: string) {
  const hash = hashStringToNumber(value);
  const index = hash % colors.length;

  return colors[index];
}

export function Avatar({
  color = "brand",
  src = null,
  shape = "circle",
  initials,
  alt = "",
  className,
  isPubliclyVisible = false,
  imageTransformation,
  ...props
}: AvatarProps & React.ComponentPropsWithoutRef<"span">) {
  return (
    <span
      data-slot="avatar"
      className={avatarVariants({
        shape,
        color,
        className: classNames({ "bg-secondary": src }, className),
      })}
      {...props}
    >
      {initials && !src && (
        <svg
          className="select-none fill-current text-[48px] font-medium uppercase"
          viewBox="0 0 100 100"
          aria-hidden={alt ? undefined : "true"}
        >
          {alt && <title>{alt}</title>}
          <text
            x="50%"
            y="50%"
            alignmentBaseline="middle"
            dominantBaseline="middle"
            textAnchor="middle"
            dy=".125em"
          >
            {initials}
          </text>
        </svg>
      )}
      {src &&
        (isPubliclyVisible ? (
          <img src={src} alt={alt} className="h-full w-full aspect-square" />
        ) : (
          <PrivateImg
            className="h-full w-full aspect-square"
            src={src}
            alt={alt}
            options={imageTransformation}
          />
        ))}
      {/* Add an inset border that sits on top of the image */}
      {shape !== "brand" && (
        <span
          className="ring-1 ring-inset ring-black/5 dark:ring-white/5 forced-colors:outline"
          aria-hidden="true"
        />
      )}
    </span>
  );
}
